import React, { Component, useEffect } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';

import { Bedbank } from '@luxuryescapes/contract-svc-bedbank';

import Spinner from '~/components/Common/Spinner';

import bedbankService from '~/services/BedbankService';

import { addQuery, parseSearchString } from '~/utils/url';

import PropertiesTable from './PropertiesTable';
import PullContentForm from './PullContent';
import PullPropertiesTable from './PullPropertiesTable';
import PropertiesSearchForm from './SearchForm';
import { CoreQuery, FilterQuery, Supplier, SupplierManageAction } from './type';

interface Props {
  history: any;
  location: any;
}

interface State {
  isLoading: boolean;
  isPulling: boolean;
  isModalLoading: boolean;
  modal: {
    id: string;
    action: SupplierManageAction;
    masterPropertyId?: string;
  } | null;
  count: number;
  data: Array<Bedbank.ListSupplierProperty>;
  pulledData: Array<Bedbank.ListSupplierProperty>;
  errorMessage: string | null;
}

const DEFAULT_SIZE_PER_PAGE = 15;

const UUID_REGEX = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;

const isUuid = (value: string) => UUID_REGEX.test(value);

function PageContainerNotify(props: any) {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (props.message) {
      enqueueSnackbar(props.message, { variant: 'error' });
    }
  }, [props.message, enqueueSnackbar]);

  return null;
}

class PageContainer extends Component<Props, State> {
  timer: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      isPulling: false,
      isModalLoading: false,
      modal: null,
      data: [],
      pulledData: [],
      count: 0,
      errorMessage: null,
    };
  }

  fetchData(data: CoreQuery) {
    this.setState({ isLoading: true });
    bedbankService
      .getSuppliersProperties(data)
      .then((data) => {
        this.setState({
          data: data.result,
          count: data.count,
          isLoading: false,
          errorMessage: null,
        });
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          errorMessage: error?.errors?.[0]?.message || error?.message || 'Internal Error',
        });
      });
  }

  queries(): Partial<CoreQuery> {
    const { location } = this.props;
    return parseSearchString(location.search);
  }

  currentPage() {
    return Number(this.queries().page || 1);
  }

  currentLimit() {
    return Number(this.queries().limit || DEFAULT_SIZE_PER_PAGE);
  }

  currentOrderBy() {
    return this.queries().orderBy || 'asc';
  }

  currentSortBy() {
    return this.queries().sortBy || 'hotelName';
  }

  currentFilterByName() {
    return this.queries().filterByName;
  }

  currentFilterById() {
    return this.queries().filterById;
  }

  currentFilterBySupplier() {
    return this.queries().filterBySupplier;
  }

  currentFilterBySupplierPropertyId() {
    return this.queries().filterBySupplierPropertyId;
  }

  currentFilterByMasterPropertyId() {
    return this.queries().filterByMasterPropertyId;
  }

  currentFilterByCountryCode() {
    return this.queries().filterByCountryCode;
  }

  currentFilterByRatingOp() {
    return this.queries().filterByRatingOp;
  }

  currentFilterByRatingVal() {
    return this.queries().filterByRatingVal;
  }

  currentFilterByHasRooms() {
    return this.queries().filterByHasRooms;
  }

  currentFilterByMapped() {
    return this.queries().filterByMapped;
  }

  currentFilterByMappedIsDeleted() {
    return this.queries().filterByMappedIsDeleted;
  }

  currentFilterByMapping() {
    return this.queries().filterByMapping;
  }

  currentFilterByHasImages() {
    return this.queries().filterByHasImages;
  }

  getQueryData(): CoreQuery {
    return {
      page: this.currentPage(),
      limit: this.currentLimit(),
      orderBy: this.currentOrderBy(),
      sortBy: this.currentSortBy(),
      filterById: this.currentFilterById(),
      filterByName: this.currentFilterByName(),
      filterBySupplier: this.currentFilterBySupplier(),
      filterBySupplierPropertyId: this.currentFilterBySupplierPropertyId(),
      filterByMasterPropertyId: this.currentFilterByMasterPropertyId(),
      filterByCountryCode: this.currentFilterByCountryCode(),
      filterByRatingOp: this.currentFilterByRatingOp(),
      filterByRatingVal: this.currentFilterByRatingVal(),
      filterByHasRooms: this.currentFilterByHasRooms(),
      filterByMapped: this.currentFilterByMapped(),
      filterByMappedIsDeleted: this.currentFilterByMappedIsDeleted(),
      filterByMapping: this.currentFilterByMapping(),
      filterByHasImages: this.currentFilterByHasImages(),
    };
  }

  componentDidMount() {
    this.fetchData(this.getQueryData());
  }

  componentWillUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  onPageChange = (page: number, limit: number) => {
    const { history, location } = this.props;
    const query = this.getQueryData();
    const newQuery = {
      ...query,
      page,
      limit,
    };
    const newLocation = addQuery(location, newQuery);
    history.push(newLocation);

    this.fetchData(newQuery);
  };

  onSortChange = (orderBy: CoreQuery['orderBy'], sortBy: CoreQuery['sortBy']) => {
    const { history, location } = this.props;
    const query = this.getQueryData();
    const newQuery = {
      ...query,
      orderBy,
      sortBy,
    };
    const newLocation = addQuery(location, newQuery);
    history.push(newLocation);

    this.fetchData(newQuery);
  };

  clearSearch = () => {
    const { history, location } = this.props;
    history.push({
      ...location,
      search: undefined,
    });
    window.location.reload();
  };

  handleSearchSubmit = (query?: Partial<FilterQuery>) => {
    const { history, location } = this.props;
    const allQuery = this.getQueryData();
    const newQuery = {
      ...allQuery,
      page: 1,
      filterByName: query?.filterByName,
      filterBySupplier: query?.filterBySupplier,
      filterBySupplierPropertyId: query?.filterBySupplierPropertyId,
      filterByMasterPropertyId: query?.filterByMasterPropertyId,
      filterByRatingVal: query?.filterByRatingVal,
      filterByRatingOp: query?.filterByRatingOp,
      filterByHasRooms: query?.filterByHasRooms,
      filterByHasImages: query?.filterByHasImages,
      filterByMapped: query?.filterByMapped,
      filterByMappedIsDeleted: query?.filterByMappedIsDeleted,
      filterByCountryCode: query?.filterByCountryCode,
    };
    const newLocation = addQuery(location, newQuery);
    history.push(newLocation);

    this.fetchData(newQuery);
  };

  handlePullSubmit = (supplier: Supplier, externalId: string) => {
    this.setState({ isPulling: true });
    bedbankService
      .pullSupplierPropertyContent({
        externalId,
        supplier,
      })
      .then((data) => {
        this.setState({
          pulledData: data.result,
          isPulling: false,
          errorMessage: null,
        });
      })
      .catch((error) => {
        this.setState({
          isPulling: false,
          errorMessage: error?.errors?.[0]?.message || error?.message || 'Internal Error',
        });
      });
  };

  openModal = (id: string, action: SupplierManageAction, masterPropertyId?: string) => {
    this.setState({
      modal: {
        id,
        action,
        masterPropertyId,
      },
    });
  };

  closeModal = () => {
    if (!this.state.isModalLoading) {
      this.setState({
        modal: null,
      });
    }
  };

  getModalInformation = () => {
    const modal = this.state.modal;

    const modalInfo = {
      supplierPropertyId: '',
      supplierHotelName: '',
      detachFrom: null,
      title: '',
      masterPropertyIdIsRequired: false,
      disabled: this.state.isModalLoading,
    };

    if (modal?.id) {
      const supplierData = this.state.data.find((x) => x.id === modal.id);
      if (supplierData) {
        modalInfo.supplierHotelName = supplierData.hotelName;
        modalInfo.supplierPropertyId = supplierData.supplierPropertyId;

        if (modal.action === 'detach') {
          modalInfo.title = `Detach Supplier Property`;

          const masterProperty = (supplierData.masterProperties ?? []).find((n) => n.id === modal.masterPropertyId);

          modalInfo.detachFrom = masterProperty ? `${masterProperty.id} (${masterProperty.supplier})` : '';
        }

        if (modal.action === 'master') {
          modalInfo.title = `De Couple Supplier Property`;
        }

        if (modal.action === 'attach') {
          modalInfo.title = `Attach Supplier Property`;
          modalInfo.masterPropertyIdIsRequired = true;
          modalInfo.disabled = !modal.masterPropertyId || this.state.isModalLoading;
        }
      }
    }

    return modalInfo;
  };

  checkJobsSupplierProperty = () => {
    const modal = this.state.modal;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    bedbankService
      .jobsSuppliersProperties({
        id: modal.id,
      })
      .then((data) => {
        if (!data.result.length || data.result.every((x) => x.state === 'completed')) {
          bedbankService
            .getSuppliersProperties({
              page: 1,
              limit: 1,
              sortBy: 'supplierPropertyId',
              orderBy: 'asc',
              filterById: modal.id,
              filterByMapping: this.getQueryData().filterByMapping,
            })
            .then((data) => {
              const allData = this.state.data.map((item) => (item.id === modal.id ? data.result[0] : item));
              this.setState({
                data: allData,
                isModalLoading: false,
                errorMessage: null,
                modal: null,
              });
            })
            .catch((error) => {
              this.setState({
                isModalLoading: false,
                errorMessage: error?.errors?.[0]?.message || error?.message || 'Internal Error',
              });
            });
        } else if (data.result.some((x) => x.state === 'failed')) {
          this.setState({
            isModalLoading: false,
            errorMessage: 'Job failed. Please try again.',
            modal: null,
          });
        } else if (data.result.length) {
          this.timer = setTimeout(this.checkJobsSupplierProperty, 1200);
        } else {
          this.setState({
            isModalLoading: false,
            errorMessage: 'Internal Error',
          });
        }
      })
      .catch((error) => {
        this.setState({
          isModalLoading: false,
          errorMessage: error?.errors?.[0]?.message || error?.message || 'Internal Error',
        });
      });
  };

  manageSupplierProperty = () => {
    this.setState({ isModalLoading: true });
    const modal = this.state.modal;
    bedbankService
      .manageSuppliersProperties({
        id: modal.id,
        action: modal.action,
        masterPropertyId: modal.masterPropertyId,
      })
      .then(() => {
        this.checkJobsSupplierProperty();
      })
      .catch((error) => {
        this.setState({
          isModalLoading: false,
          errorMessage: error?.errors?.[0]?.message || error?.message || 'Internal Error',
        });
      });
  };

  checkSupplierProperty = (event: React.ChangeEvent<HTMLInputElement>) => {
    const masterPropertyId = event.target.value;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      const modal = this.state.modal;
      if (!modal) return;

      this.setState({ isModalLoading: true });
      if (isUuid(masterPropertyId)) {
        bedbankService
          .checkSuppliersProperties({
            id: modal.id,
            propertyId: masterPropertyId,
          })
          .then(() => {
            this.setState({
              isModalLoading: false,
              errorMessage: null,
              modal: {
                ...modal,
                masterPropertyId,
              },
            });
          })
          .catch((error) => {
            this.setState({
              isModalLoading: false,
              errorMessage: error?.errors?.[0]?.message || error?.message || 'Internal Error',
              modal: {
                ...modal,
                masterPropertyId: undefined,
              },
            });
          });
      } else {
        this.setState({
          isModalLoading: false,
          modal: {
            ...modal,
            masterPropertyId: undefined,
          },
        });
      }
    }, 1500);
  };

  render() {
    const modalInfo = this.getModalInformation();
    const queryData = this.getQueryData();
    return (
      <Stack direction="column" spacing={2}>
        {this.state.errorMessage && <PageContainerNotify message={this.state.errorMessage} />}
        {queryData.filterByMapping && (
          <Box>
            <Typography variant="h2">Mapping</Typography>
          </Box>
        )}
        {!queryData.filterByMapping && (
          <>
            <PullContentForm handleSubmit={this.handlePullSubmit} isPulling={this.state.isPulling} />
            {this.state.pulledData.length > 0 && <PullPropertiesTable data={this.state.pulledData} />}
            <PropertiesSearchForm
              queries={queryData}
              handleSubmit={this.handleSearchSubmit}
              clearSearch={this.clearSearch}
            />
          </>
        )}
        {this.state.isLoading && <Spinner />}
        {!this.state.isLoading && (
          <PropertiesTable
            data={this.state.data}
            count={this.state.count}
            onPageChange={this.onPageChange}
            onSortChange={this.onSortChange}
            queries={queryData}
            openModal={this.openModal}
          />
        )}
        <Dialog open={!!this.state.modal} onClose={() => this.closeModal()}>
          <DialogTitle>
            <Typography fontWeight="bold" fontSize="large">
              {modalInfo.title}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: 4, minWidth: '400px' }}>
            <Stack direction="column" spacing={1}>
              <Box>
                <Typography fontWeight="bold">ID: {modalInfo.supplierPropertyId}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Hotel Name: {modalInfo.supplierHotelName}</Typography>
              </Box>
              {modalInfo.detachFrom ? (
                <>
                  <Box>
                    <Typography>--------------</Typography>
                  </Box>
                  <Box>
                    <Typography>FROM</Typography>
                  </Box>
                  <Box>
                    <Typography>{modalInfo.detachFrom}</Typography>
                  </Box>
                </>
              ) : (
                ''
              )}
              {modalInfo.masterPropertyIdIsRequired ? (
                <>
                  <Box>
                    <Typography>--------------</Typography>
                  </Box>
                  <Box>
                    <InputLabel id="comparative-supplier-label">Master Property Id</InputLabel>
                    <Input
                      fullWidth
                      name="masterPropertyId"
                      value={queryData.filterByMapping ? this.state.modal.masterPropertyId : null}
                      disabled={!!queryData.filterByMapping}
                      onChange={this.checkSupplierProperty}
                    />
                  </Box>
                </>
              ) : (
                ''
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            {this.state.isModalLoading && <Spinner size={25} />}
            <Button onClick={() => this.closeModal()} disabled={this.state.isModalLoading}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => this.manageSupplierProperty()} disabled={modalInfo.disabled}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }
}

export default PageContainer;
